import { create } from 'zustand';
import type { AuthUser } from '@/types/auth';
import { pb } from '@/core/api';
import * as auth from '@/core/auth';

type AuthState = {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isCollector: boolean;
  isApproacher: boolean;
  isMago: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  refreshAuth: () => Promise<void>;
  hydrate: () => void;
};

type SetFn = (s: Partial<AuthState>) => void;

function applyUser(set: SetFn, user: AuthUser | null): void {
  set({
    user,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
    isCollector: user?.role === 'collector',
    isApproacher: user?.role === 'approacher',
    isMago: user?.role === 'mago',
    isLoading: false,
  });
}

function createLogin(set: SetFn) {
  return async (email: string, password: string): Promise<void> => {
    applyUser(set, await auth.loginWithPassword(email, password));
  };
}

function createRefresh(set: SetFn) {
  return async (): Promise<void> => {
    try {
      applyUser(set, await auth.refreshAuth());
    } catch {
      applyUser(set, null);
    }
  };
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  isAuthenticated: false,
  isAdmin: false,
  isCollector: false,
  isApproacher: false,
  isMago: false,
  isLoading: true,
  login: createLogin(set),
  logout: () => {
    auth.logout();
    applyUser(set, null);
  },
  refreshAuth: createRefresh(set),
  hydrate: () => {
    applyUser(set, auth.getCurrentUser());
    if (pb.authStore.token) set({ isLoading: true });
  },
}));
